import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RelatedServiceLinks from "@/components/RelatedServiceLinks";
import TelLink from "@/components/TelLink";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import { CheckCircle, Phone, Heart, Shield, Star } from "lucide-react";

export type ServiceVariantSlug = "emergency-dentist" | "cdbs-dentist" | "turkish-dentist";

const variantCopy = {
  "emergency-dentist": {
    title: "Emergency Dentist",
    h1Suffix: "Same-Day Dental Care",
    intro:
      "If you have tooth pain, swelling, or a broken tooth near {suburb}, we can help. Y3 Smiles Dental offers same-day appointments for dental emergencies.",
    services: [
      "Help for toothache & infection",
      "Fix broken or cracked teeth",
      "Emergency tooth removal",
      "Wisdom tooth pain relief"
    ],
    ctaTitle: "Book an Emergency Dentist",
    note: "Emergency availability subject to clinical assessment."
  },
  "cdbs-dentist": {
    title: "CDBS Dentist",
    h1Suffix: "Children's Dental Benefits",
    intro:
      "Looking for a CDBS dentist near {suburb}? We accept the Child Dental Benefits Schedule for kids who qualify. We provide gentle, family-friendly care in a comfortable, modern clinic.",
    services: [
      "Check-ups and cleanings",
      "X-rays when needed",
      "Fillings and tooth repairs",
      "Teaching kids how to care for their teeth"
    ],
    ctaTitle: "Book a CDBS Appointment",
    note: "CDBS eligibility is assessed under Medicare guidelines."
  },
  "turkish-dentist": {
    title: "Turkish Dentist",
    h1Suffix: "Clear, Caring Communication",
    intro:
      "Do you speak Turkish? Our team can help Turkish-speaking families near {suburb}. We explain everything clearly and provide gentle dental care. Please ask for language support when you book.",
    services: [
      "Family dentistry for all ages",
      "Check-ups, cleans, and fillings",
      "Cosmetic and restorative treatments",
      "Clear treatment plans and aftercare"
    ],
    ctaTitle: "Book With Turkish Support",
    note: "Language support availability may vary and should be requested at the time of booking."
  }
} as const;

export const getServiceSeo = (variant: ServiceVariantSlug, suburbName: string) => {
  const copy = variantCopy[variant];
  const isBroadmeadows = suburbName === "Broadmeadows";
  const locationPhrase = isBroadmeadows ? `serving ${suburbName}` : `near ${suburbName}`;
  const title = `${copy.title} ${locationPhrase} | Y3 Smiles Dental`;
  const description = `${copy.title} ${locationPhrase}. Y3 Smiles Dental provides gentle, modern care with clear communication and convenient appointment options.`;
  return { title, description };
};

interface ServiceLocationTemplateProps {
  suburbName: string;
  suburbSlug: string;
  variant: ServiceVariantSlug;
  image?: string;
}

const ServiceLocationTemplate = ({ suburbName, suburbSlug, variant, image = '/og-shopfront.jpg' }: ServiceLocationTemplateProps) => {
  const copy = variantCopy[variant];
  const intro = copy.intro.replace("{suburb}", suburbName);
  const { title, description } = getServiceSeo(variant, suburbName);
  const isBroadmeadows = suburbName === "Broadmeadows";
  const locationPhrase = isBroadmeadows ? `serving ${suburbName}` : `near ${suburbName}`;

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title={title} description={description} image={image}>
        {/* Noindex,follow — these service-suburb pages are templated
            near-duplicates of /treatments/{service}/ and
            /locations/{suburb}/. We keep them live for direct deep-
            linking (ads, referrals) but tell search engines to defer
            to the authoritative pages they internally link to.
            See: https://blogs.bing.com/webmaster/november-2025/does-duplicate-content-hurt-seo-and-ai-search-visibility
        */}
        <meta name="robots" content="noindex, follow" />
      </SEO>
      <Header />
      <main className="flex-grow">
        <section className="bg-gradient-to-br from-neutral-50 via-primary/5 to-neutral-100 py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-4">
                {copy.title} {locationPhrase}
              </p>
              <h1 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                {copy.title} {locationPhrase} – {copy.h1Suffix}
              </h1>
              <p className="text-lg text-neutral-800 leading-relaxed mb-8">{intro}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="booking" size="xl" asChild className="group">
                  <TelLink clickSource={`service_location_${variant}_hero`}>
                    <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                    CALL 03 9022 4442
                  </TelLink>
                </Button>
                <Button variant="outline" size="xl" asChild>
                  <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                    Book Online
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-neutral-800 mb-6">{copy.title} Services</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {copy.services.map((service) => (
                  <li key={service} className="flex items-start gap-3 bg-white border border-primary/10 rounded-2xl p-5">
                    <CheckCircle className="w-5 h-5 text-primary mt-1" />
                    <span className="text-neutral-800">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {variant === "cdbs-dentist" && (
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="max-w-3xl mx-auto">
                <div className="bg-primary/5 border border-primary/15 rounded-2xl p-6 mb-8">
                  <p className="text-sm text-neutral-700 leading-relaxed">
                    The information on this page is intended as general education about the Child Dental Benefits Schedule (CDBS). Eligibility is assessed by Medicare and individual circumstances vary. Your dentist can help confirm whether your child qualifies before any treatment begins.
                  </p>
                </div>
                <h2 className="text-2xl font-bold text-neutral-800 mb-4">About the Child Dental Benefits Schedule</h2>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  The Child Dental Benefits Schedule (CDBS) is a Medicare-funded programme that provides eligible children aged 0-17 with access to basic dental services. Eligible children can receive up to $1,158 in benefits over a two-year period, covering services such as check-ups, cleaning, fillings, and X-rays. Orthodontic and cosmetic dental work is not covered under the CDBS.
                </p>
                <p className="text-neutral-700 leading-relaxed mb-6">
                  Y3 Smiles Dental participates in the CDBS and welcomes families from {suburbName} and surrounding suburbs. Our team can check your child's eligibility at the time of booking and explain what is covered before any treatment begins.
                </p>
                <h3 className="text-xl font-semibold text-neutral-800 mb-3">CDBS Frequently Asked Questions</h3>
                <Accordion type="single" collapsible className="space-y-2">
                  <AccordionItem value="cdbs-1" className="border border-primary/10 rounded-2xl px-4">
                    <AccordionTrigger className="text-left font-semibold text-neutral-800 py-4">
                      How do I know if my child is eligible for CDBS?
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-700 pb-4">
                      Eligibility is determined by Medicare based on age (0-17) and whether you receive certain government payments such as Family Tax Benefit Part A. You can check eligibility through your Medicare online account or by calling Medicare. Our reception team can also help you confirm this when you call to book.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="cdbs-2" className="border border-primary/10 rounded-2xl px-4">
                    <AccordionTrigger className="text-left font-semibold text-neutral-800 py-4">
                      What dental services are covered under CDBS?
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-700 pb-4">
                      The CDBS covers basic dental services including examinations, X-rays, cleaning, fissure sealing, fillings, root canals, and extractions. Orthodontic treatment and cosmetic procedures are not covered. The total benefit is capped at $1,158 per child over a rolling two-year period.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="cdbs-3" className="border border-primary/10 rounded-2xl px-4">
                    <AccordionTrigger className="text-left font-semibold text-neutral-800 py-4">
                      Is there a gap fee for CDBS patients at Y3 Smiles Dental?
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-700 pb-4">
                      For eligible CDBS patients, we aim to bulk bill wherever the CDBS benefit covers the full cost of treatment. If additional or more complex treatment is needed that exceeds the CDBS schedule fee, we will discuss any out-of-pocket costs with you before proceeding. Prices may vary depending on individual clinical needs.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="cdbs-4" className="border border-primary/10 rounded-2xl px-4">
                    <AccordionTrigger className="text-left font-semibold text-neutral-800 py-4">
                      How often can my child use CDBS?
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-700 pb-4">
                      The CDBS benefit of up to $1,158 is available over a two-year calendar period. Once the benefit is used up, or the two-year period ends, a new amount may become available if your child remains eligible. Regular check-ups every 6 months are generally recommended for children's dental health.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        )}

        {variant === "emergency-dentist" && (
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="max-w-3xl mx-auto">
                <div className="bg-primary/5 border border-primary/15 rounded-2xl p-6 mb-8">
                  <p className="text-sm text-neutral-700 leading-relaxed">
                    The information on this page is intended as general guidance only. If you are experiencing a medical emergency, severe difficulty breathing, or swelling that is affecting your airway, please call 000 or go to your nearest hospital emergency department immediately. A clinical assessment is needed to determine the appropriate dental treatment for your situation.
                  </p>
                </div>
                <h2 className="text-2xl font-bold text-neutral-800 mb-4">What to Do in a Dental Emergency</h2>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  Dental emergencies can be stressful, but acting quickly can help protect your oral health. If you are near {suburbName} and are experiencing severe tooth pain, a broken tooth, a knocked-out tooth, or swelling around the jaw or face, call us as soon as possible. We do our best to accommodate urgent dental needs during clinic hours.
                </p>
                <ul className="space-y-3 mb-6">
                  {[
                    "Knocked-out tooth: Handle by the crown (not the root), rinse gently, and try to keep it moist. Call us immediately — time is critical.",
                    "Severe toothache: Rinse your mouth with warm water and call us. Do not place aspirin directly on the tooth or gum.",
                    "Broken or cracked tooth: Rinse with warm water and apply a cold compress to reduce swelling if present. Avoid very hot or cold foods.",
                    "Lost filling or crown: The area may be sensitive. Dental cement available from pharmacies can provide temporary relief, but please book an appointment promptly.",
                    "Swelling in the face or jaw: This may indicate an infection. Please contact us urgently or seek hospital care if swelling is severe or spreading."
                  ].map((tip) => (
                    <li key={tip.slice(0, 30)} className="flex items-start gap-3 bg-neutral-50 rounded-xl p-4">
                      <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span className="text-neutral-700 text-sm">{tip}</span>
                    </li>
                  ))}
                </ul>
                <h3 className="text-xl font-semibold text-neutral-800 mb-3">Emergency Dental Frequently Asked Questions</h3>
                <Accordion type="single" collapsible className="space-y-2">
                  <AccordionItem value="emerg-1" className="border border-primary/10 rounded-2xl px-4">
                    <AccordionTrigger className="text-left font-semibold text-neutral-800 py-4">
                      Can I get a same-day emergency dentist appointment?
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-700 pb-4">
                      We do our best to accommodate patients experiencing dental emergencies on the same day. Availability depends on the clinical schedule when you call. We recommend phoning us as early as possible. If your situation is urgent, please let our receptionist know when you call so your case can be prioritised appropriately.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="emerg-2" className="border border-primary/10 rounded-2xl px-4">
                    <AccordionTrigger className="text-left font-semibold text-neutral-800 py-4">
                      What counts as a dental emergency?
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-700 pb-4">
                      Dental emergencies generally include: severe or persistent toothache, knocked-out or dislodged teeth, broken teeth with sharp edges or pain, dental abscesses or infections with swelling, and injuries to the mouth or jaw. If you are unsure, call us and we can advise whether you need to come in urgently or can wait for a scheduled appointment.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="emerg-3" className="border border-primary/10 rounded-2xl px-4">
                    <AccordionTrigger className="text-left font-semibold text-neutral-800 py-4">
                      How much does emergency dental treatment cost?
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-700 pb-4">
                      The cost of emergency dental treatment depends on what treatment is needed following a clinical assessment. We provide a clear explanation of costs before any treatment begins. Prices may vary depending on your individual clinical needs. We accept most major health funds and can process your claim on the spot.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="emerg-4" className="border border-primary/10 rounded-2xl px-4">
                    <AccordionTrigger className="text-left font-semibold text-neutral-800 py-4">
                      When should I go to the hospital instead of a dentist?
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-700 pb-4">
                      Go to a hospital emergency department if you have swelling that is affecting your breathing or swallowing, a high fever with facial swelling, or significant jaw trauma following an accident. For all other dental emergencies, calling your dentist first is usually the appropriate step. If you call us and cannot be seen in time, we can advise you on the best next step.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        )}

        <section className="py-20 bg-neutral-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-neutral-800 mb-6">Why Choose Y3 Smiles Dental</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: Heart, title: "Gentle, patient‑centred care" },
                  { icon: Shield, title: "Modern clinic and technology" },
                  { icon: Star, title: "Convenient location near {suburb}" }
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="bg-white border border-primary/10 rounded-2xl p-6">
                      <Icon className="w-8 h-8 text-primary mx-auto mb-3" />
                      <p className="font-semibold text-neutral-800">
                        {item.title.replace("{suburb}", suburbName)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-neutral-800 mb-4">{copy.ctaTitle}</h2>
              <p className="text-lg text-neutral-800 mb-6">Call <strong>03 9022 4442</strong> or book online today.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="booking" size="xl" asChild>
                  <TelLink clickSource={`service_location_${variant}_footer_cta`}>Call Now</TelLink>
                </Button>
                <Button variant="outline" size="xl" asChild>
                  <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>Book Online</a>
                </Button>
              </div>
              <p className="text-xs text-neutral-600 mt-4">{copy.note}</p>
            </div>
          </div>
        </section>

        <RelatedServiceLinks
          suburbName={suburbName}
          suburbSlug={suburbSlug}
          currentVariant={variant}
          className="bg-neutral-50"
        />

        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-neutral-800 mb-4">Explore Our Services</h2>
              <ul className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                {[
                  { label: "Children's Dentistry", to: "/treatments/childrens-dentistry/" },
                  { label: "Emergency Dentist", to: "/treatments/emergency/" },
                  { label: "Dental Fillings", to: "/treatments/dental-fillings/" },
                  { label: "Root Canal Treatment", to: "/treatments/root-canal/" },
                  { label: "Dental Crowns and Bridges", to: "/treatments/crown-bridge/" },
                  { label: "Dental Implants", to: "/treatments/dental-implants/" },
                  { label: "Wisdom Teeth Removal", to: "/treatments/wisdom-teeth/" },
                  { label: "Teeth Whitening", to: "/treatments/teeth-whitening/" },
                  { label: "View All Treatments", to: "/treatments/" },
                ].map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className="text-primary hover:underline text-sm font-medium">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="text-center">
                <Link to={`/locations/${suburbSlug}`} className="text-primary font-semibold hover:underline">
                  Back to {suburbName} dentist page
                </Link>
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServiceLocationTemplate;
